/**
 * Supervised Generation API service — section-by-section content review.
 *
 * Two API surfaces:
 * - Legacy session-based: /supervised/sessions/:id (TDD scaffold)
 * - Content-based (P1.7): /supervised/content/:id (spec-aligned)
 */
import { apiClient } from './apiClient';
import { useTenantStore } from '@/stores/useTenantStore';

const getTenantId = () => useTenantStore.getState().currentTenant;

// ---------------------------------------------------------------------------
// Legacy session-based types
// ---------------------------------------------------------------------------

export interface SupervisedSection {
  section_id: string;
  heading: string;
  block_type: string;
  order: number;
  status: 'not_started' | 'ready' | 'approved' | 'edited' | 'regenerating';
  content: Record<string, unknown> | null;
}

export interface SupervisedState {
  current: string;
  mode: string;
  history: Array<{ from: string; to: string; action: string }>;
}

export interface SupervisedSession {
  session_id: string;
  content_id: string;
  mode: string;
  state: SupervisedState;
  sections: { sections: SupervisedSection[] };
  created_at: string;
  updated_at: string;
}

export interface SessionListItem {
  session_id: string;
  content_id: string;
  mode: string;
  current_state: string;
  created_at: string;
}

export interface SectionActionResponse {
  status: string;
  section_id: string;
}

export interface SessionActionResponse {
  status: string;
  session_id: string;
}

// ---------------------------------------------------------------------------
// Content-based types (P1.7 — matches backend Pydantic models)
// ---------------------------------------------------------------------------

export interface TipTapBlock {
  type: string;
  attrs?: Record<string, unknown>;
  content?: Array<{ type: string; text?: string; marks?: unknown[] }>;
}

export interface SectionScore {
  total: number;
  grade: string;
  formatting?: number;
  factual_accuracy?: number;
  originality?: number;
  search_intent?: number;
  brand_voice?: number;
}

export interface SectionReviewData {
  content_id: string;
  section_index: number;
  total_sections: number;
  heading: string;
  blocks: TipTapBlock[];
  score: SectionScore | null;
  regeneration_count: number;
  is_last_section: boolean;
  approved_sections_count: number;
  auto_approve_active: boolean;
  target_words: number;
}

export interface BenchmarkResult {
  overall_score: number;
  grade: string;
  [category: string]: unknown;
}

export interface SupervisedProgressData {
  content_id: string;
  generation_mode: string;
  current_section_index: number;
  total_sections: number;
  approved_count: number;
  auto_approved_count: number;
  status: string;
  edit_count: number;
  regen_count: number;
  section_headings: string[];
  benchmark: BenchmarkResult | null;
}

export type SectionAction = 'approve' | 'edit' | 'regenerate';

export interface UserSelections {
  media?: Record<string, Array<{ type: string; checked: boolean; search_query: string; placement: string; alt_text: string }>>;
  links?: { topics: Array<{ topic: string; section_id: string; checked: boolean }>; custom_urls: Array<{ url: string; label: string }> };
  blocks?: Array<{ block_id: string; block_type: string; checked: boolean; content: any }>;
}

export interface SectionActionPayload {
  action: SectionAction;
  edited_blocks?: TipTapBlock[];
  feedback?: string;
  user_selections?: UserSelections;
}

export interface SectionActionResult {
  status: string;
  action: string;
  content_id: string;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export async function createSession(
  contentId: string,
  mode: string = 'section_by_section',
): Promise<{ session_id: string; mode: string }> {
  const { data } = await apiClient.post(
    '/supervised/sessions',
    { content_id: contentId, mode },
    { params: { tenant_id: getTenantId() } },
  );
  return data;
}

export async function getSession(sessionId: string): Promise<SupervisedSession> {
  const { data } = await apiClient.get<SupervisedSession>(
    `/supervised/sessions/${sessionId}`,
    { params: { tenant_id: getTenantId() } },
  );
  return data;
}

export async function listSessions(): Promise<{ items: SessionListItem[] }> {
  const { data } = await apiClient.get<{ items: SessionListItem[] }>(
    '/supervised/sessions',
    { params: { tenant_id: getTenantId() } },
  );
  return data;
}

export async function approveSection(
  sessionId: string,
  sectionId: string,
): Promise<SectionActionResponse> {
  const { data } = await apiClient.post<SectionActionResponse>(
    `/supervised/sessions/${sessionId}/sections/${sectionId}/approve`,
    undefined,
    { params: { tenant_id: getTenantId() } },
  );
  return data;
}

export async function editSection(
  sessionId: string,
  sectionId: string,
  content: Record<string, unknown>,
): Promise<SectionActionResponse> {
  const { data } = await apiClient.post<SectionActionResponse>(
    `/supervised/sessions/${sessionId}/sections/${sectionId}/edit`,
    { content },
    { params: { tenant_id: getTenantId() } },
  );
  return data;
}

export async function regenerateSection(
  sessionId: string,
  sectionId: string,
): Promise<SectionActionResponse> {
  const { data } = await apiClient.post<SectionActionResponse>(
    `/supervised/sessions/${sessionId}/sections/${sectionId}/regenerate`,
    undefined,
    { params: { tenant_id: getTenantId() } },
  );
  return data;
}

export async function completeSession(
  sessionId: string,
): Promise<SessionActionResponse> {
  const { data } = await apiClient.post<SessionActionResponse>(
    `/supervised/sessions/${sessionId}/complete`,
    undefined,
    { params: { tenant_id: getTenantId() } },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Content-based API calls (P1.7 — spec-aligned)
// ---------------------------------------------------------------------------

/**
 * GET /supervised/content/:id/section-review
 * Returns the current section under review with blocks, score, and progress.
 */
export async function getSectionReview(contentId: string, viewSection?: number): Promise<SectionReviewData> {
  const params: any = { tenant_id: getTenantId() };
  if (viewSection != null) params.view_section = viewSection;
  const { data } = await apiClient.get<SectionReviewData>(
    `/supervised/content/${contentId}/section-review`,
    { params },
  );
  return data;
}

/**
 * POST /supervised/content/:id/section-action
 * Submit approve/edit/regenerate for the current section.
 * Returns immediately — generation happens in background.
 */
export async function submitSectionAction(
  contentId: string,
  payload: SectionActionPayload,
): Promise<SectionActionResult> {
  const { data } = await apiClient.post<SectionActionResult>(
    `/supervised/content/${contentId}/section-action`,
    payload,
    { params: { tenant_id: getTenantId() } },
  );
  return data;
}

/**
 * GET /supervised/content/:id/progress
 * Lightweight polling endpoint for supervised generation progress.
 */
export async function getSupervisedProgress(contentId: string): Promise<SupervisedProgressData> {
  const { data } = await apiClient.get<SupervisedProgressData>(
    `/supervised/content/${contentId}/progress`,
    { params: { tenant_id: getTenantId() } },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Outline editing API calls
// ---------------------------------------------------------------------------

export interface OutlineEditPayload {
  sections: Array<{
    section_id: string;
    heading: string;
    target_words: number;
    key_points: string[];
    theme: string;
    zone: string;
  }>;
  title?: string;
  meta_description?: string;
}

export interface OutlineRegeneratePayload {
  feedback?: string;
}

export interface SectionRegeneratePayload {
  section_index: number;
  feedback?: string;
}

export interface OutlineFeedbackPayload {
  feedback: string;
}

export async function editOutline(contentId: string, payload: OutlineEditPayload) {
  const { data } = await apiClient.post(
    `/supervised/content/${contentId}/outline-edit`,
    payload,
    { params: { tenant_id: getTenantId() } },
  );
  return data;
}

export async function regenerateOutline(contentId: string, payload: OutlineRegeneratePayload) {
  const { data } = await apiClient.post(
    `/supervised/content/${contentId}/outline-regenerate`,
    payload,
    { params: { tenant_id: getTenantId() } },
  );
  return data;
}

export async function regenerateSectionOutline(contentId: string, payload: SectionRegeneratePayload) {
  const { data } = await apiClient.post(
    `/supervised/content/${contentId}/section-regenerate`,
    payload,
    { params: { tenant_id: getTenantId() } },
  );
  return data;
}

export async function saveOutlineFeedback(contentId: string, payload: OutlineFeedbackPayload) {
  const { data } = await apiClient.post(
    `/supervised/content/${contentId}/outline-feedback`,
    payload,
    { params: { tenant_id: getTenantId() } },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Generation Artifacts
// ---------------------------------------------------------------------------

export interface StrategyArtifactData {
  content_md: string | null;
  content_json: Record<string, unknown> | null;
  version: number;
  created_at: string;
}

/**
 * GET /generation-artifacts/:contentId/strategy
 * Fetch the full strategy markdown artifact for display.
 */
export async function getStrategyArtifact(contentId: string): Promise<StrategyArtifactData> {
  const { data } = await apiClient.get<StrategyArtifactData>(
    `/generation-artifacts/${contentId}/strategy`,
    { params: { tenant_id: getTenantId() } },
  );
  return data;
}
